import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { supabase } from '../lib/supabase';

/**
 * Take or pick a profile photo. Returns a base64 data URI string or null if cancelled.
 * Uses Capacitor Camera on native, file picker on web.
 */
export async function takePhoto(): Promise<string | null> {
  if (Capacitor.isNativePlatform()) {
    return takePhotoNative();
  }
  return pickPhotoWeb();
}

async function takePhotoNative(): Promise<string | null> {
  try {
    const photo = await Camera.getPhoto({
      resultType: CameraResultType.Base64,
      source: CameraSource.Prompt,
      quality: 90,
      allowEditing: false,
    });
    if (photo.base64String) {
      return `data:image/${photo.format};base64,${photo.base64String}`;
    }
    return null;
  } catch {
    return null;
  }
}

function pickPhotoWeb(): Promise<string | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) { resolve(null); return; }
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    };
    // Handle cancel (no file selected)
    input.addEventListener('cancel', () => resolve(null));
    input.click();
  });
}

/**
 * Upload a base64 data URI avatar to Supabase Storage and return the public URL.
 * Path: avatars/{userId}/avatar.{ext}
 */
export async function uploadAvatar(userId: string, dataUri: string): Promise<string | null> {
  try {
    // Parse data URI → binary
    const match = dataUri.match(/^data:image\/(\w+);base64,(.+)$/);
    if (!match) return null;
    const ext = match[1] === 'jpeg' ? 'jpg' : match[1];
    const base64 = match[2];
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const contentType = `image/${match[1]}`;

    const path = `${userId}/avatar.${ext}`;
    const { error } = await supabase.storage
      .from('avatars')
      .upload(path, bytes, { contentType, upsert: true });

    if (error) return null;

    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
    // Append cache-buster so updated avatars show immediately
    return `${data.publicUrl}?t=${Date.now()}`;
  } catch {
    return null;
  }
}

/**
 * Capture a photo for roster import (higher quality, no crop).
 * Returns base64 data + mediaType matching ImageInput interface.
 */
export async function captureRosterPhoto(): Promise<{
  base64Data: string;
  mediaType: 'image/jpeg' | 'image/png' | 'image/webp';
} | null> {
  try {
    const photo = await Camera.getPhoto({
      resultType: CameraResultType.Base64,
      source: CameraSource.Prompt,
      quality: 90,
      allowEditing: false,
    });
    if (photo.base64String) {
      const mediaType = `image/${photo.format}` as 'image/jpeg' | 'image/png' | 'image/webp';
      return { base64Data: photo.base64String, mediaType };
    }
    return null;
  } catch {
    return null;
  }
}
